import { useEffect, useState } from "react";

import { useAppConfig } from "../AppConfigProvider";
import {
  useGetCoursesSummaryBySchoolQuery,
  useGetStudentsSummaryBySchoolQuery,
  useGetCourseCompletionSummaryBySchoolQuery,
  useGetCourseAnalyticsByIdQuery,
  useGetDegreesBySchoolByQueryQuery,
  useGetCoursesByDegreeByQueryQuery,
  useGetStudentDetailedAnalyticsQuery,
} from "../graphql/generated/graphql";

export const useAnalyticsData = ({
  searchQuery,
  page,
  pageSize,
  sortBy,
}: {
  searchQuery: string;
  page: number;
  pageSize: number;
  sortBy: string;
}) => {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const [courseIdForDetailedCourse, setCourseIdForDetailedCourse] = useState<string | null>(null);
  const [degreeIdForDetailedDegree, setDegreeIdForDetailedDegree] = useState<string | null>(null);
  const [studentIdForDetailedStudent, setStudentIdForDetailedStudent] = useState<string | null>(null);

  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError,
    refetch: coursesRefetch,
  } = useGetCoursesSummaryBySchoolQuery({
    variables: { schoolId, searchQuery, page, pageSize, sortBy },
    skip: !schoolId,
  });

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
    refetch: studentsRefetch,
  } = useGetStudentsSummaryBySchoolQuery({
    variables: { schoolId, searchQuery, page, pageSize, sortBy },
    skip: !schoolId,
  });

  const {
    data: completionData,
    loading: completionLoading,
    error: completionError,
    refetch: completionRefetch,
  } = useGetCourseCompletionSummaryBySchoolQuery({
    variables: { schoolId, searchQuery, page, pageSize, sortBy },
    skip: !schoolId,
  });

  const {
    data: detailedCourseData,
    loading: detailLoading,
    refetch: detailedCourseRefetch,
  } = useGetCourseAnalyticsByIdQuery({
    variables: { courseId: courseIdForDetailedCourse },
    skip: !courseIdForDetailedCourse,
  });

  const {
    data: degreesData,
    loading: degreesLoading,
    refetch: degreesRefetch,
  } = useGetDegreesBySchoolByQueryQuery({
    variables: { schoolId, query: searchQuery, page, pageSize, sortBy },
    skip: !schoolId,
  });

  const {
    data: coursesByDegreeData,
    loading: coursesByDegreeLoading,
    refetch: coursesByDegreeRefetch,
  } = useGetCoursesByDegreeByQueryQuery({
    variables: { degreeId: degreeIdForDetailedDegree, query: "", page: 1, pageSize: 10, sortBy: "name_asc" },
    skip: !degreeIdForDetailedDegree,
  });

  const {
    data: studentDetailedAnalyticsData,
    loading: studentDetailedAnalyticsLoading,
    refetch: studentDetailedAnalyticsRefetch,
  } = useGetStudentDetailedAnalyticsQuery({
    variables: { studentId: studentIdForDetailedStudent, schoolId },
    skip: !studentIdForDetailedStudent || !schoolId,
  });

  useEffect(() => {
    if (studentIdForDetailedStudent) {
      studentDetailedAnalyticsRefetch();
    }
  }, [studentIdForDetailedStudent, studentDetailedAnalyticsRefetch]);

  useEffect(() => {
    const fetchDetailedCourse = async () => {
      if (courseIdForDetailedCourse) {
        await detailedCourseRefetch();
      }
    };
    fetchDetailedCourse();
  }, [courseIdForDetailedCourse, detailedCourseRefetch]);

  useEffect(() => {
    const fetchCoursesByDegree = async () => {
      if (degreeIdForDetailedDegree) {
        await coursesByDegreeRefetch();
      }
    };
    fetchCoursesByDegree();
  }, [degreeIdForDetailedDegree, coursesByDegreeRefetch]);

  const refetchData = async () => {
    await coursesRefetch();
    await studentsRefetch();
    await completionRefetch();
    await degreesRefetch();
  };

  const isLoading =
    coursesLoading ||
    studentsLoading ||
    completionLoading ||
    detailLoading ||
    degreesLoading ||
    coursesByDegreeLoading ||
    studentDetailedAnalyticsLoading;

  return {
    coursesData: coursesData?.getCoursesSummaryBySchool,
    studentsData: studentsData?.getStudentsSummaryBySchool,
    completionData: completionData?.getCourseCompletionSummaryBySchool,
    detailedCourse: detailedCourseData?.getCourseAnalyticsById?.data?.[0] || null,
    degreesData: degreesData?.getDegreesBySchoolByQuery?.data,
    coursesByDegreeData: coursesByDegreeData?.getCoursesByDegreeByQuery?.data,
    studentDetailedAnalyticsData: studentDetailedAnalyticsData?.getStudentDetailedAnalytics?.data?.[0] || null,
    setCourseIdForDetailedCourse,
    setDegreeIdForDetailedDegree,
    setStudentIdForDetailedStudent,
    isLoading,
    studentDetailedAnalyticsLoading,
    refetchData,
  };
};
